import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ScreenLoader } from './ScreenLoader';
import type { LoaderKind } from './loaderMessages';

/**
 * Bir ekranın açılışını tek göstergede toplar.
 *
 * Master-detay ekranında master grid ile detay grid'i ayrı ayrı yükleniyor ve her biri
 * kendi göstergesini çiziyordu: aynı anda iki logo, biri bitince öteki dönmeye devam.
 * Artık ekrandaki bekleyenlerin hepsi buraya kaydoluyor, hepsi bitene kadar TEK bir
 * gösterge ekranı örtüyor.
 *
 * Örtü opak: altta yarı kurulmuş grid'in sütun başlıkları belirip kaybolmasın. Grid'ler
 * yine de normal render ediliyor (gizlenmiyor), yoksa sanal kaydırma genişliği sıfır
 * ölçüp açılışta boş satır çiziyor.
 *
 * Örtü yalnızca İLK açılışta var. Sonraki filtre/yenileme beklemelerinde ekranın tamamını
 * kapatmak, kullanıcının baktığı veriyi elinden almak olurdu — orada grid kendi
 * göstergesini çiziyor.
 */
interface ScreenLoadApi {
  /** Bu iş bitene kadar ekranı açılmış sayma. Dönen işlev bekletmeyi bırakır. */
  hold: () => () => void;
  /** Örtüyü beklemeden kaldır: ekran açıldı, sıra kullanıcıda. */
  reveal: () => void;
  /** İlk açılış örtüsü hâlâ ekranda: bileşen kendi göstergesini çizmesin. */
  covered: boolean;
}

const ScreenLoadContext = createContext<ScreenLoadApi | null>(null);

export function useScreenLoad(): ScreenLoadApi | null {
  return useContext(ScreenLoadContext);
}

/**
 * `active` olduğu sürece ekranın açılışını bekletir.
 * Dönen değer: örtü ekrandaysa `true` — çağıran kendi göstergesini çizmemeli.
 */
export function useScreenHold(active: boolean): boolean {
  const api = useScreenLoad();
  useEffect(() => {
    if (!api || !active) return;
    return api.hold();
  }, [api, active]);
  return api?.covered ?? false;
}

/**
 * `active` olduğu anda örtüyü kaldırır — toleransı da beklemeden.
 *
 * Kullanıcıya bir şey sorulduğunda (SP parametre dialogu) ekran artık "açılıyor" değil:
 * bekleten sunucu değil kullanıcının kendisi. Dialogun arkasında dönen gösterge hem
 * yanlış hem dikkat dağıtıcıydı.
 */
export function useScreenReveal(active: boolean) {
  const api = useScreenLoad();
  useEffect(() => {
    if (api && active) api.reveal();
  }, [api, active]);
}

/** Suspense fallback'i olarak: lazy parça inene kadar açılışı bekletir, kendi bir şey çizmez. */
export function SuspenseHold() {
  useScreenHold(true);
  return null;
}

/** Bekleyen hiç kaydolmazsa örtüyü bu kadar sonra kaldır (ekran gerçekten boş). */
const ARM_MS = 250;
/**
 * Sayaç sıfırlanınca hemen açma, bu kadar bekle.
 *
 * Açılış zincir halinde ilerliyor: demet iner → grid kurulur → veri ister → master satırı
 * seçilir → detay veri ister. Her halkanın arasında sayaç bir an sıfıra düşüyor; tolerans
 * olmadan örtü orada kalkıp ekranı yarım gösteriyor, sonra grid kendi göstergesini
 * çiziyordu (ölçümde "Ekran açılıyor… → Veriler getiriliyor…" sıçraması buydu).
 */
const GAP_MS = 250;
/** Bir bekleyen hiç bitmezse ekran sonsuza kadar kapalı kalmasın. */
const SAFETY_MS = 25_000;

export function ScreenLoadProvider({ kind = 'screen', children }: { kind?: LoaderKind; children: ReactNode }) {
  const [holds, setHolds] = useState(0);
  const [phase, setPhase] = useState<'arming' | 'loading' | 'done'>('arming');
  const holdsRef = useRef(0);

  const hold = useCallback(() => {
    holdsRef.current += 1;
    setHolds(holdsRef.current);
    let released = false;
    return () => {
      if (released) return;
      released = true;
      holdsRef.current -= 1;
      setHolds(holdsRef.current);
    };
  }, []);

  // Hiç bekleyen kaydolmadıysa örtüyü tutma: ekran tipi kendi başına hazır olabilir.
  useEffect(() => {
    if (phase !== 'arming') return;
    const id = setTimeout(() => setPhase((p) => (p === 'arming' ? 'done' : p)), ARM_MS);
    return () => clearTimeout(id);
  }, [phase]);

  useEffect(() => {
    if (holds > 0) {
      setPhase((p) => (p === 'done' ? p : 'loading'));
      return;
    }
    const id = setTimeout(() => setPhase((p) => (p === 'loading' ? 'done' : p)), GAP_MS);
    return () => clearTimeout(id);
  }, [holds]);

  useEffect(() => {
    if (phase !== 'loading') return;
    const id = setTimeout(() => setPhase('done'), SAFETY_MS);
    return () => clearTimeout(id);
  }, [phase]);

  const reveal = useCallback(() => setPhase('done'), []);

  const covered = phase !== 'done';
  const api = useMemo<ScreenLoadApi>(() => ({ hold, reveal, covered }), [hold, reveal, covered]);

  return (
    <ScreenLoadContext.Provider value={api}>
      <div className="relative flex min-h-0 flex-1 flex-col">
        {children}
        {covered ? <ScreenLoader kind={kind} overlay="opaque" /> : null}
      </div>
    </ScreenLoadContext.Provider>
  );
}
